import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import {
    History,
    Store,
    Package,
    Calendar,
    Loader2,
    AlertTriangle,
    Info
} from 'lucide-react';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import { useToast } from '../hooks/useToast';
import {
    fetchStores,
    fetchCatentriesForStore,
    fetchUtilCatentriesForStore,
    fetchStockHistoryReport,
    currencyCodeToSymbolMap
} from '../apiHelper';
import TrialModal from '../components/ui/TrialModal';

// Helper function to format date to YYYY-MM-DD
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const StockHistory = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();

    const [user, setUser] = useState(null);

    // State for stores
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const [loadingStores, setLoadingStores] = useState(false);
    const [errorStores, setErrorStores] = useState(null);

    // State for items (catentries)
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loadingItems, setLoadingItems] = useState(false);
    const [errorItems, setErrorItems] = useState(null);
    const [hasSetInitialItem, setHasSetInitialItem] = useState(false);

    // State for date range
    const [startDate, setStartDate] = useState(formatDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
    const [endDate, setEndDate] = useState(formatDate(new Date()));

    // State for report data
    const [reportData, setReportData] = useState([]);
    const [loadingReport, setLoadingReport] = useState(false);
    const [errorReport, setErrorReport] = useState(null);
    const [currencySymbol, setCurrencySymbol] = useState('$');

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    // Memoize chart data for performance
    const chartData = useMemo(() => {
        if (!reportData || reportData.length === 0) return [];
        return reportData.map(event => ({
            datetime: new Date(event.datetime).getTime(),
            running_quantity: event.running_quantity,
        })).sort((a, b) => a.datetime - b.datetime);
    }, [reportData]);

    // === ALL ORIGINAL useEffect HOOKS — 100% UNCHANGED ===
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view stock history.", "info");
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            const loadStores = async () => {
                setLoadingStores(true);
                setErrorStores(null);
                try {
                    const fetchedStores = await fetchStores(parsedUser);
                    const formattedStores = fetchedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id,
                        storeData: store
                    }));
                    setStores(formattedStores);
                    if (formattedStores.length > 0) {
                        setSelectedStore(formattedStores[0]);
                    }
                } catch (err) {
                    const errorMessage = err.response?.data?.detail || `Failed to load stores: ${err.message || err}`;
                    setErrorStores(errorMessage);
                    showToast(errorMessage, 'error');
                } finally {
                    setLoadingStores(false);
                }
            };
            loadStores();
        }
    }, [showToast, navigate]);

    useEffect(() => {
        const loadItems = async () => {
            // Only run if we have the necessary data
            if (!user || !selectedStore) {
                setItems([]);
                setSelectedItem(null);
                return; // Removed setHasSetInitialItem(false) from here to prevent loops
            }

            setLoadingItems(true);
            setErrorItems(null);
            try {
                const payload = { store_id: selectedStore.value };
                const fetchedItems = await fetchUtilCatentriesForStore(payload);

                // console.log('Fetched:', fetchedItems);
                const formattedItems = fetchedItems.map(item => ({
                    label: item.productname,
                    value: item.catentry_id,
                }));
                // console.log('Formatted:', formattedItems);

                setItems(formattedItems);

                // Logic to determine which item to select
                const catentryIdFromState = location.state?.catentryId;

                if (catentryIdFromState && !hasSetInitialItem) {
                    const matchingItem = formattedItems.find(item => item.value === catentryIdFromState);
                    setSelectedItem(matchingItem || formattedItems[0] || null);
                    if (!matchingItem) showToast("Selected item not found in this store.", "warning");
                } else if (!hasSetInitialItem) {
                    setSelectedItem(formattedItems[0] || null);
                }

                // Set this last
                setHasSetInitialItem(true);

            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load items: ${err.message || err}`;
                setErrorItems(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoadingItems(false);
            }
        };

        loadItems();
        // REMOVED hasSetInitialItem from dependencies to stop the double-log
    }, [user, selectedStore, location.state, showToast]);

    useEffect(() => {
        setHasSetInitialItem(false);
    }, [location.state]);

    useEffect(() => {
        const loadReport = async () => {
            if (!user || !selectedStore || !selectedItem || !startDate || !endDate) {
                setReportData([]);
                setCurrencySymbol('$');
                return;
            }
            setLoadingReport(true);
            setErrorReport(null);
            try {
                const payload = {
                    users_id: user.users_id,
                    catentry_id: selectedItem.value,
                    store_id: selectedStore.value,
                    start_date: startDate,
                    end_date: endDate
                };
                const response = await fetchStockHistoryReport(payload);
                console.log(response)
                setReportData(response.events);
                setCurrencySymbol(currencyCodeToSymbolMap[response.default_currency_code] || '$');
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load report: ${err.message || err}`;
                setErrorReport(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoadingReport(false);
            }
        };
        loadReport();
    }, [user, selectedStore, selectedItem, startDate, endDate, showToast]);

    // Early return if not logged in
    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view this page.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 pb-8">
            {/* Page Header */}
            <div className="px-4 sm:px-6 lg:px-8 pt-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-text-primary flex items-center gap-3">
                    <History size={28} className="text-gmail-blue" />
                    Stock History Report
                </h1>
            </div>

            {/* Filters Toolbar */}
            <div className="px-4 sm:px-6 lg:px-8 mt-6">
                <Card className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Store */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1.5">
                                <Store size={14} className="inline mr-1 text-gmail-green" />
                                Select Store
                            </label>
                            <Select
                                options={stores}
                                value={selectedStore}
                                onChange={setSelectedStore}
                                isLoading={loadingStores}
                                isDisabled={loadingStores}
                                placeholder="Search store..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                            {errorStores && <p className="text-red-500 text-xs mt-1">{errorStores}</p>}
                        </div>

                        {/* Item */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1.5">
                                <Package size={14} className="inline mr-1 text-gmail-yellow" />
                                Select Item
                            </label>
                            <Select
                                options={items}
                                value={selectedItem}
                                onChange={setSelectedItem}
                                isLoading={loadingItems}
                                isDisabled={!selectedStore || loadingItems}
                                placeholder="Search item..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                            {errorItems && <p className="text-red-500 text-xs mt-1">{errorItems}</p>}
                        </div>

                        {/* Start Date */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1.5">
                                <Calendar size={14} className="inline mr-1 text-gmail-red" />
                                Start Date
                            </label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>

                        {/* End Date */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 mb-1.5">
                                <Calendar size={14} className="inline mr-1 text-gmail-red" />
                                End Date
                            </label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Report Table */}
            <div className="flex-1 px-4 sm:px-6 lg:px-8 mt-6">
                <Card className="h-full flex flex-col overflow-hidden">
                    {loadingReport ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-text-secondary">
                            <Loader2 className="animate-spin h-10 w-10 mb-4 text-gmail-blue" />
                            <p className="text-lg">Generating report...</p>
                        </div>
                    ) : errorReport ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-red-600">
                            <AlertTriangle className="h-10 w-10 mb-4" />
                            <p className="text-lg font-semibold">{errorReport}</p>
                            <p className="text-sm text-gray-500 mt-2">Please check your selections and try again.</p>
                        </div>
                    ) : reportData.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                            <Info className="h-10 w-10 mb-4" />
                            <p className="text-lg font-semibold">No data found for the selected criteria.</p>
                            <p className="text-sm mt-2">Try adjusting the date range or selecting a different item.</p>
                        </div>
                    ) : (
                        <div className="flex-1 overflow-auto custom-scrollbar">
                            {/* Mobile: Card List */}
                            <div className="block md:hidden">
                                {reportData.map((row, index) => (
                                    <div key={index} className="bg-white border-b border-gray-200 p-4">
                                        <div className="font-medium text-gray-900 mb-3">
                                            {new Date(row.datetime).toLocaleString()}
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-gray-600">Event</div>
                                            <div>{row.event_type.replace(/_/g, ' ')}</div>

                                            <div className="text-gray-600">Qty Change</div>
                                            <div className="text-right font-mono">
                                                {row.quantity_change > 0 ? `+${row.quantity_change}` : row.quantity_change}
                                            </div>

                                            <div className="text-gray-600">Unit Cost</div>
                                            <div className="text-right font-mono">{currencySymbol}{row.unit_cost.toFixed(2)}</div>

                                            <div className="text-gray-600">Running Qty</div>
                                            <div className="text-right font-mono">{row.running_quantity}</div>

                                            <div className="text-gray-600">Running Value</div>
                                            <div className="text-right font-mono">{currencySymbol}{row.running_value.toFixed(2)}</div>

                                            <div className="text-gray-600">Avg Cost</div>
                                            <div className="text-right font-mono">{currencySymbol}{row.running_average_cost.toFixed(2)}</div>

                                            <div className="text-gray-600">Source</div>
                                            <div>{row.source_document_type.replace(/_/g, ' ')} ({row.source_document_id})</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Tablet & Desktop: Full Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity Change</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Cost</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Running Qty</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Running Value</th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Cost</th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {reportData.map((row, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {new Date(row.datetime).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {row.event_type.replace(/_/g, ' ')}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                                                    {row.quantity_change > 0 ? `+${row.quantity_change}` : row.quantity_change}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                                                    {currencySymbol}{row.unit_cost.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                                                    {row.running_quantity}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                                                    {currencySymbol}{row.running_value.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                                                    {currencySymbol}{row.running_average_cost.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {row.source_document_type.replace(/_/g, ' ')} ({row.source_document_id})
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default StockHistory;